import { useMemo, useState } from "react";
import { Share2, Check, Copy, Mail, MessageCircle, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { useCopyToClipboard } from "@/lib/useCopyToClipboard";
import { shuffle, buildSmsHref } from "@/lib/shareMessages";

/**
 * A button that opens a share sheet for a donation page: the phone's own
 * share menu first (when there is one), email and text, the link to
 * copy, and a few ready-made messages. StudentShareDialog and
 * SchoolShareDialog supply the copy.
 *
 * Props:
 *   url, title, description   — the page, and the dialog's heading
 *   messages                  — [{ kind, label, body }], shuffled on open
 *   emailSubject, shareTitle  — for the email link and native share
 *   triggerLabel, triggerVariant, triggerSize, triggerClassName, triggerFullWidth
 */
export default function ShareDialog({
  url,
  title,
  description,
  messages,
  emailSubject,
  shareTitle,
  triggerLabel = "Share",
  triggerVariant = "outline",
  triggerSize = "sm",
  triggerClassName = "",
  triggerFullWidth = false,
}) {
  const [open, setOpen] = useState(false);
  // Reshuffle each time it opens so the same suggestion isn't always first.
  const ordered = useMemo(() => (open ? shuffle(messages) : messages), [open, messages]);

  return (
    <>
      <Button
        variant={triggerVariant}
        size={triggerSize}
        className={`${triggerFullWidth ? "w-full" : ""} ${triggerClassName}`}
        onClick={() => setOpen(true)}
      >
        <Share2 className="h-4 w-4" /> {triggerLabel}
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} title={title} description={description} className="max-w-lg">
        <ShareOptions url={url} messages={ordered} emailSubject={emailSubject} shareTitle={shareTitle} />
      </Dialog>
    </>
  );
}

function ShareOptions({ url, messages, emailSubject, shareTitle }) {
  const primary = messages[0]?.body || url;
  const canNativeShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const nativeShare = async () => {
    try {
      await navigator.share({ title: shareTitle, text: primary, url });
    } catch {
      // Cancelled — nothing to do.
    }
  };
  const emailHref = `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(primary)}`;

  return (
    <div className="space-y-6">
      {canNativeShare && (
        <Button size="lg" className="w-full" onClick={nativeShare}>
          <Share2 className="h-4 w-4" /> Share…
        </Button>
      )}

      <div className="grid grid-cols-2 gap-2">
        <ShareLink href={emailHref} icon={Mail} label="Email" />
        <ShareLink href={buildSmsHref(primary)} icon={MessageCircle} label="Text message" />
      </div>

      <LinkBlock url={url} primary={!canNativeShare} />

      <section>
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Or copy a message</h3>
        <ul className="space-y-3">
          {messages.map((m) => <MessageRow key={m.kind} message={m} />)}
        </ul>
      </section>
    </div>
  );
}

function ShareLink({ href, icon: Icon, label }) {
  return (
    <a
      href={href}
      className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-border bg-card px-3 text-sm font-medium text-foreground transition-colors hover:bg-secondary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Icon className="h-4 w-4" />
      {label}
    </a>
  );
}

function LinkBlock({ url, primary }) {
  const [copied, copy] = useCopyToClipboard();
  return (
    <div className="flex items-stretch gap-2 rounded-lg border border-border bg-background p-2">
      <div className="min-w-0 flex-1 truncate px-2 py-2 font-mono text-xs text-foreground sm:text-sm">{url}</div>
      <Button type="button" size="sm" variant={primary && !copied ? "default" : "outline"} onClick={() => copy(url)} className="shrink-0">
        {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Link2 className="h-4 w-4" /> Copy link</>}
      </Button>
    </div>
  );
}

function MessageRow({ message }) {
  const [copied, copy] = useCopyToClipboard();
  return (
    <li className="space-y-2 rounded-lg border border-border bg-background p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{message.label}</span>
        <Button type="button" size="sm" variant="outline" onClick={() => copy(message.body)} className="h-9 px-3 text-xs">
          {copied ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><Copy className="h-3.5 w-3.5" /> Copy</>}
        </Button>
      </div>
      <p className="text-sm leading-relaxed text-foreground">{message.body}</p>
    </li>
  );
}
